import React, { useState, useEffect } from "react";
import { Edit, Trash2, Plus, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./DistrictList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const DistrictList = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  const fetchDistricts = async () => {
    try {
      // Fetch based on toggle
      const endpoint = showInactive ? `${url}/api/district/all` : `${url}/api/district`;
      const res = await axios.get(endpoint);
      setDistricts(res.data.data || []);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to fetch districts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this district?")) {
      try {
        const res = await axios.delete(`${url}/api/district/${id}`);
        alert(res.data.message || "District deactivated successfully");
        fetchDistricts(); // Refresh the list
      } catch (err) {
        alert(err.response?.data?.message || "Failed to deactivate district");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this district?")) {
      try {
        const res = await axios.put(`${url}/api/district/${id}/restore`);
        alert(res.data.message || "District activated successfully");
        fetchDistricts(); // Refresh the list
      } catch (err) {
        alert(err.response?.data?.message || "Failed to activate district");
      }
    }
  };

  if (loading) return <div className="loading">Loading districts...</div>;

  return (
    <div className="district-container">
      <div className="header">
        <h2>Districts List</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
            style={{
              padding: '8px 16px',
              backgroundColor: showInactive ? '#4a5568' : '#718096',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button
            onClick={() => navigate("/AddDistrict")}
            className="add-button"
          >
            <Plus size={16} /> Add District
          </button>
        </div>
      </div>

      <table className="district-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th style={{ textAlign: "center", width: "150px" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {districts.length === 0 ? (
            <tr>
              <td colSpan="3" className="no-data">
                No districts found
              </td>
            </tr>
          ) : (
            districts.map((district) => (
              <tr key={district._id} className={district.status === 'inactive' ? 'inactive-row' : ''}>
                <td>{district.name}</td>
                <td>
                  <span className={`status-badge ${district.status}`}>
                    {district.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="actions-cell">
                  <div className="actions">
                    {district.status === 'active' ? (
                      <>
                        <button
                          onClick={() => navigate(`/district/edit/${district._id}`)}
                          className="edit-btn"
                          title="Edit"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleSoftDelete(district._id)}
                          className="delete-btn"
                          title="Deactivate"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleRestore(district._id)}
                        className="restore-btn"
                        title="Activate"
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        <RotateCcw size={14} /> Activate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <style jsx>{`
        .inactive-row {
          background-color: #f3f4f6;
          opacity: 0.7;
        }
        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        .status-badge.active {
          background-color: #10b981;
          color: white;
        }
        .status-badge.inactive {
          background-color: #ef4444;
          color: white;
        }
        .toggle-button {
          transition: all 0.3s ease;
        }
        .toggle-button:hover {
          opacity: 0.9;
        }
        .restore-btn:hover {
          background-color: #059669 !important;
        }
      `}</style>
    </div>
  );
};

export default DistrictList;